import { Object3D, MeshBasicMaterial, FrontSide, NoBlending, Material } from 'three'
import type { WebGLProgramParametersWithUniforms } from 'three'
import type { TextLabel } from '@/lib/three-d-resources/labels-manager'
import type { DrillLabelApiData } from '~/entities/drillLabel'

export function nodeId(node: Object3D): number | undefined {
  return node.userData.nodeId
}

// TypeScript util to be used with a switch or if-conditional block
// Put this in the `default` case to make sure you never get there.
export function exhaustive(anything: never): never {
  throw new Error('Check was not exhaustive')
}

export function labelsScaleFactor(labels: readonly TextLabel[]): number {
  const maxTextSize = Math.max.apply(
    null,
    labels.filter(l => l.visible && !l.autoScale).map(l => l.textSize),
  )

  // Scale down text sizes > 100 which render poorly on Firefox.
  return Math.max(1, maxTextSize / 100)
}

export function createMeshThumbnailMaterial(): Material {
  const meshThumbnailMaterial = new MeshBasicMaterial({ color: 0x000000 })
  meshThumbnailMaterial.side = FrontSide
  meshThumbnailMaterial.opacity = 0
  meshThumbnailMaterial.transparent = true
  meshThumbnailMaterial.blending = NoBlending

  meshThumbnailMaterial.onBeforeCompile = (shader: WebGLProgramParametersWithUniforms) => {
    // define varying vUv in vertex shader
    shader.vertexShader = shader.vertexShader.replace(
      `#include <uv_pars_vertex>`,
      `#define USE_UV
      #include <uv_pars_vertex>`,
    )
    // define varying vUv in fragment shader
    shader.fragmentShader = shader.fragmentShader.replace(
      `#include <uv_pars_fragment>`,
      `#define USE_UV
      #include <uv_pars_fragment>`,
    )
    shader.fragmentShader = shader.fragmentShader.replace(
      `#include <dithering_fragment>`,
      `#include <dithering_fragment>
      ${thumbnailFragmentShader}`,
    )
  }

  return meshThumbnailMaterial
}

const thumbnailFragmentShader = /* glsl */ `
float xMargin = 0.15;
float yMargin = 0.4;
vec4 black = vec4(0., 0., 0., 1.);
vec4 white = vec4(1., 1., 1., 1.);

vec4 color = mix(black, white, step(xMargin, vUv.x) - step(1. - xMargin, vUv.x)); // horizontal margin
color = mix(black, color, step(yMargin, vUv.y) - step(1. - yMargin, vUv.y));      // vertical margin
gl_FragColor = color;
`

export function groupDataBy(
  getGroupId: (item: DrillLabelApiData) => string | number,
  labels: DrillLabelApiData[],
): { [key: string]: DrillLabelApiData[] } {
  return labels.reduce((acc: { [key: string]: DrillLabelApiData[] }, item: DrillLabelApiData) => {
    const key = getGroupId(item)

    if (!acc[key]) {
      acc[key] = []
    }

    acc[key].push(item)
    return acc
  }, {})
}

export function getDrillLabelName(label: DrillLabelApiData): string {
  const collapsedInterceptInfo = label.edited_collapsed_intercept_info ?? label.collapsed_intercept_info ?? ''
  const expandedInterceptInfo = label.edited_expanded_intercept_info ?? label.expanded_intercept_info ?? ''
  const subInterceptInfo = label.edited_sub_intercept_info ?? label.sub_intercept_info ?? ''
  const holeId = label.hole_id ?? ''

  return `${holeId} ${collapsedInterceptInfo} ${expandedInterceptInfo}${subInterceptInfo}`
}
